import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getPreferredPrinterConfig,
  setPreferredPrinterConfig,
} from '../../utils/printerPreferences';
import appVersion from '../../utils/appVersion';

const HandheldSettingsPage = () => {
  const { logout } = useAuth();
  const [labelConfig, setLabelConfig] = useState(() => {
    const cfg = getPreferredPrinterConfig('label') || {};
    return { ...cfg, port: String(cfg.port || 9100) };
  });
  const [receiptConfig, setReceiptConfig] = useState(() => {
    const cfg = getPreferredPrinterConfig('receipt') || {};
    return { ...cfg, port: String(cfg.port || 9100) };
  });
  const [message, setMessage] = useState('');

  const handleSave = (type, config) => {
    setPreferredPrinterConfig(type, {
      ...config,
      port: Number(config.port) || undefined,
    });
    const latest = getPreferredPrinterConfig(type);
    const formatted = { ...latest, port: String(latest.port || 9100) };
    if (type === 'label') {
      setLabelConfig(formatted);
    } else {
      setReceiptConfig(formatted);
    }
    setMessage(`Saved ${type} printer preference.`);
    setTimeout(() => setMessage(''), 2500);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Device settings</p>
        <h1 className="text-2xl font-semibold text-slate-900">Printer preferences</h1>
        <p className="text-sm text-slate-500">
          Store the Bluetooth names used by your handheld so label and receipt jobs pair with the correct device every time.
        </p>
        {message && <p className="text-sm text-green-600">{message}</p>}
      </div>

      <div className="bg-white rounded-2xl shadow p-4 space-y-4">
        <section>
          <h2 className="text-lg font-semibold text-slate-900">Label printer</h2>
          <p className="text-sm text-slate-500 mb-2">
            Choose Bluetooth or a network-connected label printer for handheld queues.
          </p>
          <div className="flex flex-wrap gap-4 text-sm mb-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="settingsLabelTransport"
                value="bluetooth"
                checked={labelConfig.transport === 'bluetooth'}
                onChange={() => setLabelConfig((prev) => ({ ...prev, transport: 'bluetooth' }))}
              />
              Bluetooth
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="settingsLabelTransport"
                value="network"
                checked={labelConfig.transport === 'network'}
                onChange={() => setLabelConfig((prev) => ({ ...prev, transport: 'network' }))}
              />
              Network (LAN)
            </label>
          </div>
          {labelConfig.transport === 'bluetooth' ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={labelConfig.bluetoothName || ''}
                onChange={(e) => setLabelConfig((prev) => ({ ...prev, bluetoothName: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-xl px-3 py-2"
                placeholder="Label printer name"
              />
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-slate-900 text-white"
                onClick={() => handleSave('label', labelConfig)}
              >
                Save
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                <input
                  type="text"
                  value={labelConfig.host || ''}
                  onChange={(e) => setLabelConfig((prev) => ({ ...prev, host: e.target.value }))}
                  className="sm:col-span-2 border border-gray-300 rounded-xl px-3 py-2"
                  placeholder="Printer IP address"
                />
                <input
                  type="number"
                  min="1"
                  max="65535"
                  value={labelConfig.port || ''}
                  onChange={(e) => setLabelConfig((prev) => ({ ...prev, port: e.target.value }))}
                  className="border border-gray-300 rounded-xl px-3 py-2"
                  placeholder="Port"
                />
              </div>
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-slate-900 text-white"
                onClick={() => handleSave('label', labelConfig)}
              >
                Save
              </button>
            </>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900">Receipt printer</h2>
          <p className="text-sm text-slate-500 mb-2">
            Used for handheld checkout receipts or test prints from the app.
          </p>
          <div className="flex flex-wrap gap-4 text-sm mb-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="settingsReceiptTransport"
                value="bluetooth"
                checked={receiptConfig.transport === 'bluetooth'}
                onChange={() => setReceiptConfig((prev) => ({ ...prev, transport: 'bluetooth' }))}
              />
              Bluetooth
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="settingsReceiptTransport"
                value="network"
                checked={receiptConfig.transport === 'network'}
                onChange={() => setReceiptConfig((prev) => ({ ...prev, transport: 'network' }))}
              />
              Network (LAN)
            </label>
          </div>
          {receiptConfig.transport === 'bluetooth' ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={receiptConfig.bluetoothName || ''}
                onChange={(e) => setReceiptConfig((prev) => ({ ...prev, bluetoothName: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-xl px-3 py-2"
                placeholder="Receipt printer name"
              />
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-slate-900 text-white"
                onClick={() => handleSave('receipt', receiptConfig)}
              >
                Save
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                <input
                  type="text"
                  value={receiptConfig.host || ''}
                  onChange={(e) => setReceiptConfig((prev) => ({ ...prev, host: e.target.value }))}
                  className="sm:col-span-2 border border-gray-300 rounded-xl px-3 py-2"
                  placeholder="Printer IP address"
                />
                <input
                  type="number"
                  min="1"
                  max="65535"
                  value={receiptConfig.port || ''}
                  onChange={(e) => setReceiptConfig((prev) => ({ ...prev, port: e.target.value }))}
                  className="border border-gray-300 rounded-xl px-3 py-2"
                  placeholder="Port"
                />
              </div>
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-slate-900 text-white"
                onClick={() => handleSave('receipt', receiptConfig)}
              >
                Save
              </button>
            </>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900">Tips</h2>
          <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
            <li>Ensure the printer is powered on and paired with the device via Android Bluetooth settings.</li>
            <li>Use distinct names for label vs receipt printers when both are in range.</li>
            <li>Printer preferences are stored per device; repeat this step on new handhelds.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900">App info</h2>
          <p className="text-sm text-slate-600">BoltinPOS build {appVersion}</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900">Session</h2>
          <p className="text-sm text-slate-500 mb-3">
            Sign out from this handheld when the shift is complete or before handing the device to another staff member.
          </p>
          <button
            type="button"
            className="px-4 py-2 rounded-xl bg-red-600 text-white"
            onClick={logout}
          >
            Logout
          </button>
        </section>
      </div>
    </div>
  );
};

export default HandheldSettingsPage;
